import React, { useCallback, useState } from 'react';
import Button from '@material-ui/core/Button';
import styled from 'styled-components';
import {
  withRouter,
} from 'react-router-dom';
import * as qs from 'qs';
import { CircularProgress } from '@material-ui/core';
import { GB, ES } from 'country-flag-icons/react/3x2';
import LoginForm from '../components/forms/LoginForm';
import { useUser } from '../contexts/UserContext';
import { useVendingMachine } from '../contexts/VendingMachineContext';
import ResetPasswordDialog from '../components/forms/ResetPasswordDialog';
import Footer from '../components/Footer';
import Logo from '../images/logo_elecctro_colored.png';
import i18n from '../i18n/i18n';
import { useTheme } from '../contexts/ThemeContext';
import { login } from '../api/api';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100dvh;
  width: 100dvw;
  overflow-x: hidden;
`;

const LogoContainer = styled.div`
  height: 26px;
  text-align: center;
  font-size: 0.4rem;
  vertical-align: middle;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  flex:1
`;

const StyledButton = styled(({ ...rest }) => (
  <Button
    {...rest}
  />
))`
  && {
    background-color: ${(props) => props.color};
    border-width: 0;
    border-radius: 10px;
    padding: 10px 20px;
    margin: 10px;
    width: 120px;
    text-transform: capitalize;
    font: 0.85em ${(props) => props.font};
    color: ${(props) => props.textColor};

    ${({ disabled }) => disabled && `
     background-color: rgb(243, 243, 243);
     color: rgba(255,255,255,0.15)
     border-width: 0;
     border-radius: 10px;
     padding: 10px 20px;
     margin: 10px;
     width: 120px;
     text-transform: capitalize;
     font: 0.85em ${(props) => props.font};
  `
}
`;

const StyledLink = styled.a`
  text-decoration: underline;
  color: ${(props) => props.color};
`;

const Spacer = styled.div`
  margin-bottom: 60px;
`;

const HelexiaInput = styled.input`
  background: transparent;
  border: none;
  outline: none;
  color: #3f3f3f;
  font-family: Geologica, sans-serif;
  font-weight: 500;
  font-size: 14px;
  letter-spacing: -0.98px;
  width: 100%;
  &::placeholder {
    color: #6a6a6a;
  }
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0px 1000px #efefef inset;
    box-shadow: 0 0 0px 1000px #efefef inset;
    -webkit-text-fill-color: #3f3f3f;
  }
`;

const LoginView = ({ location, history }) => {
  const { loggedIn, signIn } = useUser();
  const { stickerId, vendingMachineInfo } = useVendingMachine();
  const [hasError, setHasError] = React.useState(false);
  const [email, setEmail] = React.useState<string | null>(null);
  const [password, setPassword] = React.useState<string | null>(null);
  const [isOpenResetPasswordDialog, setOpenResetPasswordDialog] = React.useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [loginError, setLoginError] = useState<string | null>(null);
  const { s } = qs.parse(location.search, { ignoreQueryPrefix: true });
  const { theme, themeName } = useTheme();
  const isHelexia = themeName === 'helexia';
  const isGalp = themeName === 'galp';
  const [galpLang, setGalpLang] = useState<string>(i18n.language);
  const [galpLangOpen, setGalpLangOpen] = useState<boolean>(false);

  const handleError = useCallback((error) => {
    setHasError(error);
  }, []);

  React.useEffect(() => {
    if (loggedIn && s && !stickerId) {
      history.push(`load?s=${s}`);
      return;
    }

    if (loggedIn && !vendingMachineInfo) {
      history.push('home-no-vm');
      return;
    }

    if (loggedIn && vendingMachineInfo) {
      history.push(`load?s=${stickerId}`);
      return;
    }

    if (!loggedIn && s && !stickerId) {
      history.push(`load?s=${s}`);
    }

    setLoading(false);
  }, [loggedIn, stickerId, history]);

  const handleHelexiaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    try {
      const response = await login(email as string, password as string);
      signIn({ user: { email, id: response.id }, token: response.token });
    } catch (ex: any) {
      setLoginError(i18n.t(ex?.body?.message === 'INVALID_DATA' ? 'login_data_invalid' : 'error_occured_try_later') as string);
    }
  };

  const handleGalpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    try {
      const response = await login(email as string, password as string);
      signIn({ user: { email, id: response.id }, token: response.token });
    } catch (ex: any) {
      const messageKey = ex?.body?.message === 'INVALID_DATA' ? 'login_data_invalid' : 'error_occured_try_later';
      setLoginError(i18n.t(messageKey) as string);
    }
  };

  const handleGalpLanguageChange = (newLanguage: string) => {
    setGalpLang(newLanguage);
    i18n.changeLanguage(newLanguage);
    localStorage.setItem('pay.elecctro.language', newLanguage);
    setGalpLangOpen(false);
  };

  if (isHelexia) {
    return loading ? (
      <Root>
        <CircularProgress variant="indeterminate" style={{ color: theme.loadingIconColor }} />
      </Root>
    ) : (
      <div style={{
        width: '100%',
        height: '100dvh',
        background: 'white',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
      >
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px 12px',
          overflow: 'hidden',
        }}
        >
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '40px',
            width: '100%',
            overflow: 'hidden',
          }}
          >
            <img
              src="/companies/HELEXIA/helexia_footer_logo.svg"
              alt="Helexia"
              style={{ width: '112px', height: '25px' }}
            />
            <p style={{
              fontFamily: 'Geologica, sans-serif',
              fontWeight: 600,
              fontSize: '32px',
              lineHeight: '32px',
              color: '#004e74',
              textAlign: 'center',
              margin: 0,
              width: '100%',
            }}
            >
              {i18n.t('welcome')}
            </p>
            <form
              onSubmit={handleHelexiaSubmit}
              style={{ display: 'flex', flexDirection: 'column', gap: '40px', width: '100%' }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%' }}>
                <div style={{
                  background: '#efefef',
                  borderRadius: '12px',
                  height: '36px',
                  padding: '0 10px',
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
                >
                  <HelexiaInput
                    type="email"
                    placeholder="Email"
                    value={email || ''}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{
                    background: '#efefef',
                    borderRadius: '12px',
                    height: '36px',
                    padding: '0 10px',
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                  >
                    <HelexiaInput
                      type="password"
                      placeholder={i18n.t('password') as string}
                      value={password || ''}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                  {loginError && (
                    <p style={{
                      color: '#d01818',
                      fontFamily: 'Geologica, sans-serif',
                      fontWeight: 500,
                      fontSize: '12px',
                      margin: '4px 0 0 0',
                    }}
                    >
                      {loginError}
                    </p>
                  )}
                </div>
              </div>
              <button
                type="submit"
                disabled={!email || !password}
                style={{
                  background: (!email || !password) ? '#d0d0d0' : 'linear-gradient(90deg, #fbd802 0%, #f7c749 100%)',
                  borderRadius: '12px',
                  height: '36px',
                  border: 'none',
                  cursor: (!email || !password) ? 'not-allowed' : 'pointer',
                  color: 'white',
                  fontFamily: 'Geologica, sans-serif',
                  fontWeight: 400,
                  fontSize: '14px',
                  letterSpacing: '-0.42px',
                  width: '100%',
                }}
              >
                {i18n.t('login')}
              </button>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '40px',
                alignItems: 'center',
                width: '100%',
              }}
              >
                <p style={{
                  fontFamily: 'Geologica, sans-serif',
                  fontWeight: 500,
                  fontSize: '14px',
                  color: '#6b6b6b',
                  letterSpacing: '-0.98px',
                  textAlign: 'center',
                  margin: 0,
                }}
                >
                  {i18n.t('forgot_password')}
                  <br />
                  <span
                    role="button"
                    tabIndex={0}
                    style={{ color: '#004e74', textDecoration: 'underline', cursor: 'pointer' }}
                    onClick={() => setOpenResetPasswordDialog(true)}
                    onKeyDown={(e) => e.key === 'Enter' && setOpenResetPasswordDialog(true)}
                  >
                    {i18n.t('click_here')}
                  </span>
                </p>
                <p style={{
                  fontFamily: 'Geologica, sans-serif',
                  fontWeight: 500,
                  fontSize: '14px',
                  color: '#6b6b6b',
                  letterSpacing: '-0.98px',
                  textAlign: 'center',
                  margin: 0,
                }}
                >
                  {i18n.t('no_account')}
                  {' '}
                  <span
                    role="button"
                    tabIndex={0}
                    style={{ color: '#004e74', textDecoration: 'underline', cursor: 'pointer' }}
                    onClick={() => history.push({ pathname: 'register', state: { email, password } })}
                    onKeyDown={(e) => e.key === 'Enter' && history.push({ pathname: 'register', state: { email, password } })}
                  >
                    {i18n.t('register')}
                  </span>
                </p>
              </div>
            </form>
          </div>
        </div>
        <div style={{
          flexShrink: 0,
          background: 'white',
          boxShadow: '0px -4px 3.5px rgba(0,0,0,0.05)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px 20px',
        }}
        >
          <span style={{
            fontFamily: 'Lato, sans-serif',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '20px',
            color: '#58595b',
          }}
          >
            powered by
          </span>
          <img src={Logo} alt="elecctro" style={{ height: '25px', display: 'inline' }} />
        </div>
        <ResetPasswordDialog
          email={email}
          onClose={() => setOpenResetPasswordDialog(false)}
          open={isOpenResetPasswordDialog}
        />
      </div>
    );
  }

  if (isGalp) {
    const galpLanguages = [
      { value: 'ptPT', code: 'PT' },
      { value: 'esES', code: 'ES' },
      { value: 'enGB', code: 'EN' },
    ];
    const flagComponents: { [key: string]: any } = { esES: ES, enGB: GB };
    const currentLang = galpLanguages.find((l) => l.value === galpLang) || galpLanguages[0];
    const canSubmit = !!email && !!password;
    const renderFlag = (value: string) => {
      if (value === 'ptPT') {
        return (
          <img
            src="/companies/GALP/flag_pt.svg"
            alt="PT"
            style={{ width: '21px', height: '16px', display: 'block' }}
          />
        );
      }
      const FlagComp = flagComponents[value];
      return (
        <span style={{
          width: '21px', height: '16px', borderRadius: '3px', overflow: 'hidden', display: 'block',
        }}
        >
          <FlagComp style={{ width: '21px', height: '16px', display: 'block' }} />
        </span>
      );
    };

    return loading ? (
      <Root>
        <CircularProgress variant="indeterminate" style={{ color: theme.loadingIconColor }} />
      </Root>
    ) : (
      <div style={{
        width: '100%',
        height: '100dvh',
        background: 'white',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        fontFamily: theme.bodyFont,
      }}
      >
        {/* Header strip — cream, logo centered, language selector on the right */}
        <div style={{
          position: 'relative',
          background: '#f7f4ed',
          minHeight: '95px',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          padding: '23px 16px 18px',
          flexShrink: 0,
          boxSizing: 'border-box',
        }}
        >
          <img src={theme.logo} alt="Galp" style={{ height: '33px', display: 'block' }} />
          <div style={{
            position: 'absolute',
            right: '16px',
            top: '23px',
          }}
          >
            <button
              type="button"
              onClick={() => setGalpLangOpen((o) => !o)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                fontFamily: theme.bodyFont,
              }}
            >
              {renderFlag(currentLang.value)}
              <span style={{ fontSize: '14px', color: '#0b0b0b' }}>{currentLang.code}</span>
              <svg
                width="10"
                height="6"
                viewBox="0 0 10 6"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ display: 'block' }}
              >
                <path
                  d="M1 1L5 5L9 1"
                  stroke="#0b0b0b"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {galpLangOpen && (
              <div style={{
                position: 'absolute',
                top: 'calc(100% + 6px)',
                right: 0,
                background: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                boxShadow: '0px 2px 8px rgba(0,0,0,0.12)',
                zIndex: 10,
                overflow: 'hidden',
                minWidth: '92px',
              }}
              >
                {galpLanguages.map(({ value, code }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => handleGalpLanguageChange(value)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      width: '100%',
                      padding: '8px 12px',
                      background: value === galpLang ? '#f7f4ed' : 'white',
                      border: 'none',
                      cursor: 'pointer',
                      fontFamily: theme.bodyFont,
                      fontSize: '14px',
                      color: '#0b0b0b',
                    }}
                  >
                    {renderFlag(value)}
                    {code}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Body — title, inputs, links */}
        <form
          id="login"
          onSubmit={handleGalpSubmit}
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            padding: '32px 16px',
            boxSizing: 'border-box',
          }}
        >
          <p style={{
            fontFamily: theme.titleFont,
            fontSize: '18px',
            fontWeight: 500,
            lineHeight: '22px',
            color: theme.titleColor,
            textAlign: 'center',
            margin: 0,
          }}
          >
            {i18n.t('login')}
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '13px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <label htmlFor="galp-email" style={{ fontSize: '14px', lineHeight: '22px', color: '#111110' }}>
                {i18n.t('email')}
              </label>
              <div style={{
                border: '2px solid #bab9ba',
                borderRadius: '14px',
                height: '46px',
                display: 'flex',
                alignItems: 'center',
                padding: '0 10px',
                boxSizing: 'border-box',
              }}
              >
                <input
                  id="galp-email"
                  type="email"
                  value={email || ''}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    width: '100%',
                    fontFamily: theme.bodyFont,
                    fontSize: '16px',
                    color: theme.titleColor,
                  }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <label htmlFor="galp-password" style={{ fontSize: '14px', lineHeight: '22px', color: '#111110' }}>
                {i18n.t('password')}
              </label>
              <div style={{
                border: '2px solid #bab9ba',
                borderRadius: '14px',
                height: '46px',
                display: 'flex',
                alignItems: 'center',
                padding: '0 10px',
                boxSizing: 'border-box',
              }}
              >
                <input
                  id="galp-password"
                  type="password"
                  value={password || ''}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    width: '100%',
                    fontFamily: theme.bodyFont,
                    fontSize: '16px',
                    color: theme.titleColor,
                  }}
                />
              </div>
              {loginError && (
                <p style={{
                  color: '#d01818', fontSize: '12px', margin: '4px 0 0 0',
                }}
                >
                  {loginError}
                </p>
              )}
            </div>
          </div>

          <div style={{
            display: 'flex', flexDirection: 'column', gap: '12px', textAlign: 'center',
          }}
          >
            <span
              role="button"
              tabIndex={0}
              onClick={() => setOpenResetPasswordDialog(true)}
              onKeyDown={(e) => e.key === 'Enter' && setOpenResetPasswordDialog(true)}
              style={{
                color: theme.linkColor, fontSize: '14px', textDecoration: 'none', cursor: 'pointer',
              }}
            >
              {i18n.t('forgot_password_link')}
            </span>
            <span
              role="button"
              tabIndex={0}
              onClick={() => history.push('/session/documents')}
              onKeyDown={(e) => e.key === 'Enter' && history.push('/session/documents')}
              style={{
                color: theme.linkColor, fontSize: '14px', textDecoration: 'none', cursor: 'pointer',
              }}
            >
              {i18n.t('search_documents')}
            </span>
          </div>
        </form>

        {/* Footer — Entrar (outline) over Registar (solid) */}
        <div style={{
          borderTop: '1px solid #bababa',
          padding: '32px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          flexShrink: 0,
          boxSizing: 'border-box',
        }}
        >
          <button
            type="submit"
            form="login"
            disabled={!canSubmit}
            style={{
              height: '36px',
              borderRadius: '57px',
              background: 'white',
              border: `1px solid ${canSubmit ? theme.buttonColor : '#bab9ba'}`,
              color: canSubmit ? theme.buttonColor : '#888887',
              fontFamily: theme.bodyFont,
              fontSize: '14px',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              boxShadow: '0px 2px 0px rgba(0,0,0,0.04)',
            }}
          >
            {i18n.t('login')}
          </button>
          <button
            type="button"
            onClick={() => history.push({ pathname: 'register', state: { email, password } })}
            style={{
              height: '36px',
              borderRadius: '57px',
              background: theme.buttonColor,
              border: 'none',
              color: theme.buttonTextColor,
              fontFamily: theme.bodyFont,
              fontSize: '14px',
              cursor: 'pointer',
              boxShadow: '0px 2px 0px rgba(0,0,0,0.04)',
            }}
          >
            {i18n.t('register')}
          </button>
        </div>

        <ResetPasswordDialog
          email={email}
          onClose={() => setOpenResetPasswordDialog(false)}
          open={isOpenResetPasswordDialog}
        />
      </div>
    );
  }

  return loading ? (
    <Root>
      <CircularProgress variant="indeterminate" style={{ color: theme.loadingIconColor }} />
    </Root>
  ) : (
    <Root>
      <div style={{ width: '90%', fontFamily: theme.bodyFont }}>
        <LogoContainer>
          <img
            src={theme.logo}
            alt="Logo"
            style={{
              height: '240%',
            }}
          />
        </LogoContainer>
        <h1 style={{ marginTop: '175px' }}>{i18n.t('login')}</h1>
        <LoginForm
          handleError={handleError}
          email={email}
          setEmail={setEmail}
          password={password}
          setPassword={setPassword}
        />
        <p style={{ marginTop: 0 }}>
          <small style={{ color: 'rgba(77,76,77,255)' }}>
            {i18n.t('forgot_password')}
            &nbsp;
            <StyledLink
              color={theme.linkColor}
              onClick={() => setOpenResetPasswordDialog(true)}
            >
              {i18n.t('click_here')}
            </StyledLink>
          </small>
        </p>
        <p style={{ marginTop: 0 }}>
          <small style={{ color: 'rgba(77,76,77,255)' }}>
            {i18n.t('search_documents')}
            ?
            &nbsp;
            <StyledLink
              color={theme.linkColor}
              onClick={() => history.push('/session/documents')}
            >
              {i18n.t('click_here')}
            </StyledLink>
          </small>
        </p>
        <ButtonsContainer>
          <StyledButton
            variant="outlined"
            color={theme.buttonColor}
            textColor={theme.buttonTextColor}
            font={theme.bodyFont}
            type="submit"
            form="login"
            disabled={hasError}
          >
            {i18n.t('login')}
          </StyledButton>
          <StyledButton
            variant="outlined"
            color={theme.buttonColor}
            textColor={theme.buttonTextColor}
            font={theme.bodyFont}
            onClick={() => history.push({ pathname: 'register', state: { email, password } })}
          >
            {i18n.t('register')}
          </StyledButton>
        </ButtonsContainer>
        <ResetPasswordDialog
          email={email}
          onClose={() => setOpenResetPasswordDialog(false)}
          open={isOpenResetPasswordDialog}
        />
      </div>
      <Spacer />
      <div style={{
        height: '20px',
        padding: '12.5px 0px',
        fontSize: '0.8rem',
        userSelect: 'none',
        position: 'absolute',
        bottom: '20px',
      }}
      >
        {theme && theme.loginViewLogo
          ? (
            <img src={theme.loginViewLogo} alt="Logo" style={{ height: '195%', display: 'inline' }} />
          ) : (
            <>
              <div style={{
                minWidth: '15%',
                maxWidth: '15%',
                display: 'inline-block',
              }}
              />
              <div style={{
                minWidth: '70%', maxWidth: '70%', display: 'inline-block', height: '25px', textAlign: 'center',
              }}
              >
                <div style={{ display: 'inline', color: 'rgb(78,78,72)', fontFamily: theme.bodyFont }}>
                  powered by&nbsp;
                  <img
                    src={Logo}
                    alt="Logo"
                    style={{
                      height: '125%',
                      display: 'inline',
                    }}
                  />
                </div>
              </div>
            </>
          )}
      </div>
    </Root>
  );
};

export default withRouter(LoginView);
